import { useMemo } from "react";

import {
  DateTimeValue,
  isHourlyValue,
} from "@/offering_v2/components/date-time-picker/date_time_picker";
import {
  MIN_RECURRING_COUNT,
  RECURRENCE_FREQUENCY,
  RecurrenceInput,
  RecurrenceRule,
} from "@/offering/recurrence";
import {
  expandRecurrence,
  RecurringExpansion,
} from "@/offering_v2/utils/expand_recurrence";
import {
  RecurringAvailabilityRequest,
  useRecurringAvailabilityQuery,
} from "./use_recurring_availability_query";

// The availability request a date/time value implies: hourly needs its window,
// daily needs a single day. Undefined while the value is incomplete, so no
// request fires until there is a verdict to ask for.
export function toRecurringAvailabilityRequest(
  value: DateTimeValue | undefined
): RecurringAvailabilityRequest | undefined {
  if (!value) return undefined;
  if (isHourlyValue(value)) {
    if (!value.date || !value.startTime || !value.endTime) return undefined;
    return {
      durationUnit: "hour",
      startTime: value.startTime,
      endTime: value.endTime,
    };
  }
  if (!value.startDate || value.startDate !== value.endDate) return undefined;
  return { durationUnit: "day" };
}

export function anchorOf(value: DateTimeValue | undefined): string | undefined {
  if (!value) return undefined;
  return isHourlyValue(value) ? value.date : value.startDate;
}

// Everything a surface needs to know about a repeated selection: the horizon
// fetched for its window, the rule expanded over it, and the wire-ready
// `recurrence` for the quote. Both the picker (draft value) and the templates
// (committed value) read it; SWR dedupes the shared key between them.
export function useRecurringSelection(
  spaceId: string | undefined,
  value: DateTimeValue | undefined,
  rule: RecurrenceRule | undefined
) {
  const request = rule ? toRecurringAvailabilityRequest(value) : undefined;
  const availability = useRecurringAvailabilityQuery(spaceId, request);
  const anchor = anchorOf(value);

  const expansion: RecurringExpansion | undefined = useMemo(() => {
    if (!rule || !anchor || !availability.data) return undefined;
    return expandRecurrence(availability.data, anchor, rule);
  }, [rule, anchor, availability.data]);

  const recurrenceInput: RecurrenceInput | undefined = useMemo(() => {
    if (!rule || !expansion || expansion.booked.length < MIN_RECURRING_COUNT) {
      return undefined;
    }
    const skippedDateReasons: NonNullable<
      RecurrenceInput["skippedDateReasons"]
    > = {};
    for (const skipped of expansion.skipped) {
      if (skipped.reason) skippedDateReasons[skipped.date] = skipped.reason;
    }
    return {
      frequency: RECURRENCE_FREQUENCY,
      interval: rule.interval,
      count: expansion.booked.length,
      dates: expansion.booked,
      // Omitted entirely when no skipped date carried a reason, so the quote
      // payload stays byte-identical for the common nothing-skipped rule.
      ...(Object.keys(skippedDateReasons).length ? { skippedDateReasons } : {}),
    };
  }, [rule, expansion]);

  return {
    availability: availability.data,
    expansion,
    recurrenceInput,
    loading: availability.loading,
    error: availability.error,
    ineligible: availability.ineligible,
    // A rule is committed but its dates are not known yet. Callers hold the
    // quote rather than fall back to a single booking the customer did not ask
    // for.
    pending: !!rule && !!request && !availability.data && !availability.error,
  };
}
